"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, Mail, ArrowRight, ShieldCheck, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const serviceAreas = ["Berkshire", "Surrey", "Hampshire", "Buckinghamshire", "London & Home Counties"];
export default function Quote() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="requestquotecta" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Main CTA Panel */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col">
            <Card className="h-full bg-secondary text-secondary-foreground border-border/20 shadow-xl rounded-2xl flex flex-col justify-between overflow-hidden relative">
              {/* Subtle top accent bar */}
              <div className="h-1.5 w-full bg-primary" />

              <CardContent className="p-8 sm:p-10 md:p-12 flex-1 flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-primary-foreground text-xs font-semibold uppercase tracking-wider mb-6">
                    <ShieldCheck className="size-3.5 text-primary" />
                    Safety-led scaffolding contractor
                  </div>

                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold text-secondary-foreground tracking-tight leading-tight">
                    Request a quote for your project.
                  </h2>

                  <p className="mt-4 text-base sm:text-lg text-secondary-foreground/80 max-w-xl font-default">
                    Send over your site drawings, access requirements, or schedule needs. Our team provides clear specifications and responsive turnaround across the South East.
                  </p>
                </div>

                <div className="mt-8 pt-8 border-t border-secondary-foreground/10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 py-6 text-base font-semibold shadow-md transition-colors">
                    <Link to="/ContactRequestQuotePage">
                      Request a quote
                      <ArrowRight className="size-5 ml-1" />
                    </Link>
                  </Button>

                  <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/5 text-base font-medium transition-colors">
                    <Phone className="size-4 text-primary" />
                    <span>01344 929900</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Contact & Coverage Side Panel */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Contact Direct Cards */}
            <div className="flex flex-col gap-4">
              <Card className="bg-card text-card-foreground border-border shadow-sm rounded-xl p-6 transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
                <a href="tel:01344929900" className="flex items-start gap-4 group">
                  <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                    <Phone className="size-5" />
                  </div>
                  <div className="min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Direct telephone
                    </span>
                    <span className="block text-lg font-bold text-foreground group-hover:text-primary transition-colors truncate mt-0.5">
                      01344 929900
                    </span>
                    <span className="block text-xs text-muted-foreground mt-1">
                      Speak directly with our contracts team
                    </span>
                  </div>
                </a>
              </Card>

              <Card className="bg-card text-card-foreground border-border shadow-sm rounded-xl p-6 transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
                <a href="mailto:quotes@bmfscaffolding.co.uk" className="flex items-start gap-4 group">
                  <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                    <Mail className="size-5" />
                  </div>
                  <div className="min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Email drawings & enquiries
                    </span>
                    <span className="block text-lg font-bold text-foreground group-hover:text-primary transition-colors truncate mt-0.5">
                      quotes@bmfscaffolding.co.uk
                    </span>
                    <span className="block text-xs text-muted-foreground mt-1">
                      Send project specs or schedule requests
                    </span>
                  </div>
                </a>
              </Card>
            </div>

            {/* Coverage Summary Box */}
            <Card className="bg-muted/50 text-foreground border-border shadow-none rounded-xl p-6 flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 text-foreground font-semibold text-sm mb-3">
                <MapPin className="size-4 text-primary" />
                <span>Regional coverage</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {serviceAreas.map((area, i) => <span key={area} data-index={i} className="inline-flex items-center px-3 py-1 rounded-md bg-card text-card-foreground text-xs font-medium border border-border">
                    {area}
                  </span>)}
              </div>
              <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
                Serving roofing contractors, solar installers, main contractors, facilities managers, and commercial projects.
              </p>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}